import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
	getHistoricalAndCustomFXRates,
	putHistoricalAndCustomFXRates,
} from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { PutCustomExchangeRateDto } from '@dealstack/shared/lib/generated/models/PutCustomExchangeRateDto';
import { VALUATION_SAVE_MUTATION_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { CurrencyType } from '@dealstack/shared/lib/generated';

export function useHistoricalAndCustomFXRatesQuery(
	dealId: string | undefined,
	valuationId: string | undefined,
	defaultCurrency: CurrencyType | undefined
) {
	const query = useQuery({
		// Rates are quoted against the default currency, and changing it clears the custom ones.
		queryKey: ['fx-rates-historical', dealId ?? '', valuationId ?? '', defaultCurrency ?? ''],
		queryFn: () =>
			getHistoricalAndCustomFXRates({ dealId: dealId ?? '', valuationId: valuationId ?? '' }),
		enabled: !!dealId && !!valuationId,
		placeholderData: keepPreviousData,
	});

	const historicalAndCustomFXRates = useMemo(() => {
		const rates: Record<string, { history: number | null; custom: string | null }> = {};
		query.data?.payload?.rates.forEach((rate) => {
			// null, not undefined: on reset, react-hook-form keeps a mounted field's old value for undefined.
			rates[rate.currency] = {
				history: rate.historical,
				custom: rate.custom?.toString() ?? null,
			};
		});
		return rates;
	}, [query.data?.payload?.rates]);

	const modifiedRates = useMemo(() => {
		return (
			query.data?.payload?.rates.map((rate) => ({
				currency: rate.currency,
				rate: rate.custom ?? rate.historical ?? 1,
			})) ?? []
		);
	}, [query.data?.payload?.rates]);

	const updateMutation = useMutation({
		mutationKey: [VALUATION_SAVE_MUTATION_KEY, valuationId ?? ''],
		mutationFn: async (data: PutCustomExchangeRateDto) => {
			await putHistoricalAndCustomFXRates({
				valuationId: valuationId ?? '',
				dealId: dealId ?? '',
				data,
			});
		},
		onSuccess: async () => {
			await query.refetch();
		},
	});

	return {
		historicalAndCustomFXRates,
		modifiedRates,
		isHistoricalAndCustomRatesFetching: query.isFetching,
		refetchHistoricalAndCustomFXRates: query.refetch,
		updateCustomRates: updateMutation.mutateAsync,
	};
}
